import { useContext, useEffect } from "react";
import { Formik, FormikProps } from "formik";
import { QuotationContext } from "../../../../../contexts/QuotationContext";
import { QuotationCardContext } from "../../../../../contexts/QuotationCardContext";
import { MtplQuotation } from "../../../../../model/quotation";
import { getOwnerBirthYearOptions, VehicleGroup, VehicleType, VehicleTypeNames, VehicleGroups } from "../../../../../model/vehicle";
import { SelectOption } from "../../../../../components/inputs/SelectOneInput";
import { ISettingOption } from "../../../../../model/settingItem";
import "./MtplTarifficationInputViewStyles.scss";
import { MTPLVehicleGroupComponent } from "./MtplVehicleGroupComponent";
import { OutputLink } from "../../../../../components/OutputLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../../../../../components/Button";
import { strings } from "../../../../../strings/strings";
import otkIcon from "../../../../../images/otk_icon.svg";
import { ParametrizedField } from "../../../../../components/ParametrizedField";
import moment from "moment";
import { calculateMTPLDuration } from "../../../../../api/durationApiClient";
import { CalculatedMTPLDuration } from "../../../../../model/duration";
import { searchCity } from "../../../../../api/vehicleApiClient";
import { ProcessingContext } from "../../../../../contexts/ProcessingContext";
import {fieldsName} from "./consts";
import FocusError from "../../../../../components/FocusError";

interface MTPLTarifficationInputViewProps {
    onSubmit: (value) => void;
    vehicleGroup: VehicleGroup;
    vehicleTypes?: VehicleType[];
    onBackListener: () => void;
    constructionYearLoadFunction: () => any[];
    searchBy?: 'phoneNumber' | 'plateNumber'
}

export const MTPLTarifficationInputView = (props: MTPLTarifficationInputViewProps) => {
    const { vehicleGroup, vehicleTypes, onSubmit, onBackListener, constructionYearLoadFunction, searchBy } = props;
    const { quotation, selectedView, saveTempValues } = useContext<QuotationContext<MtplQuotation>>(QuotationContext);
    const { nextIsAvailable, loadFromServer, validateFields } = useContext(QuotationCardContext);
    const { setProcessingOpen } = useContext(ProcessingContext);

    useEffect(() => {
        loadFromServer(Object.values(fieldsName));
    }, [])

    const citiesShortcut = () => {
        return [
            { value: '8878', label: strings.KyivFull, labelShort: strings.Kyiv },
            { value: '15033', label: strings.OdesaFull, labelShort: strings.Odesa },
            { value: '12350', label: strings.LvivFull, labelShort: strings.Lviv },
            { value: '22279', label: strings.KharkivFull, labelShort: strings.Kharkiv },
            { value: '2802', label: strings.DniproFull, labelShort: strings.Dnipro },
            { value: '7449', label: strings.ZaporizhzhiaFull, labelShort: strings.Zaporizhzhia }
        ]
    };
    const vehicleTypeMapper = (value: VehicleType) => {
        return {
            label: VehicleTypeNames[value],
            value: value.toString()
        }
    };

    const vehicleTypesOptions: SelectOption[] = vehicleGroup ? VehicleGroups[vehicleGroup].map(vehicleTypeMapper) :
        vehicleTypes && vehicleTypes.length > 0 ? vehicleTypes.map(vehicleTypeMapper) : Object.values(VehicleType).map(vehicleTypeMapper);

    const onFormSubmit = (quotation: MtplQuotation) => {
        setProcessingOpen(true);

        validateFields(quotation).then(res => {
            if (res) {
                onSubmit(quotation);
            } else {
                setProcessingOpen(false)
            }
        });
    }

    const formatResponseForAutocomplete = (response: any) => {
        return {
            name: response.label,
            code: response.value,
        }
    }
    const formatSelectedOptionForAutocomplete = (option: any) => {
        return {
            label: option?.name,
            value: option?.code
        }
    }

    async function updateMTPLDuration({ duration, endDate }: CalculatedMTPLDuration, formProps: FormikProps<{ quotation: MtplQuotation }>) {
        await formProps.setFieldValue('quotation.mtplContract.duration', duration);
        await formProps.setFieldValue('quotation.mtplContract.endDate', endDate);
    }

    return (
        <Formik
            initialValues={{ quotation: quotation }}
            onSubmit={values => {
                onFormSubmit(values.quotation);
            }}
        >
            {formProps => (
                <div className={"mtpl-tariffication-input"}>
                    {saveTempValues("quotation", JSON.stringify(formProps.values.quotation))}
                    <form id={"mainForm"}>
                        <FocusError />
                        <div className="mtpl-tariffication-input__form">
                            <div className="mtpl-tariffication-input__form__group">
                                <MTPLVehicleGroupComponent
                                    vehicleGroup={vehicleGroup}
                                    plateNumber={quotation.mtplContract.vehicle.plateNumber}
                                 />
                                <div className="mtpl-tariffication-input__form__group__edit">
                                    <OutputLink
                                        label={strings.edit}
                                        onClickListener={onBackListener}
                                        icon={<FontAwesomeIcon icon={faPen as any} />}
                                    />
                                </div>
                            </div>

                            <div className="mtpl-tariffication-input__form__checkboxes">
                                <div className="mtpl-tariffication-input__form__checkboxes__items">
                                    <ParametrizedField
                                        contextName={fieldsName.vehicleTaxiUsage}
                                        quotationName={'quotation.mtplContract.'}
                                        context={QuotationCardContext}
                                    />
                                    <ParametrizedField
                                        contextName={fieldsName.vehicleRequiredTO}
                                        quotationName={'quotation.mtplContract.'}
                                        context={QuotationCardContext}
                                        onChangeListener={(val) => {
                                            if (val === false) {
                                                formProps.setFieldValue("quotation.mtplContract.vehicle.nextToDate", null);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="mtpl-tariffication-input__form__inputs">
                                <div className={"mtpl-tariffication-input__form__inputs__title"}>
                                    {strings.tarifficationTitle}
                                </div>
                                <ParametrizedField
                                    contextName={fieldsName.vehicleVehicleType}
                                    quotationName={'quotation.mtplContract.'}
                                    context={QuotationCardContext}
                                    defaultOptions={vehicleTypesOptions}
                                 />
                                <ParametrizedField
                                    contextName={fieldsName.vehicleRegisterCity}
                                    quotationName={'quotation.mtplContract.'}
                                    context={QuotationCardContext}
                                    shortcut={citiesShortcut()}
                                    charactersToSearch={3}
                                    infiniteScroll
                                    lazyDefaultOptions={(query: string, size: number) => searchCity(query, size)}
                                    formatResponse={formatResponseForAutocomplete}
                                    formatValue={formatSelectedOptionForAutocomplete}
                                    formatOptions={formatSelectedOptionForAutocomplete}
                                />
                                <ParametrizedField
                                    contextName={fieldsName.vehicleBrand}
                                    quotationName={'quotation.mtplContract.'}
                                    context={QuotationCardContext}
                                    formatResponse={formatResponseForAutocomplete}
                                    formatValue={formatSelectedOptionForAutocomplete}
                                />
                                <ParametrizedField
                                    contextName={fieldsName.vehicleOwnerPartyType}
                                    quotationName={'quotation.mtplContract.'}
                                    context={QuotationCardContext}
                                />
                                <ParametrizedField
                                    contextName={fieldsName.vehicleConstructionYear}
                                    quotationName={'quotation.mtplContract.'}
                                    context={QuotationCardContext}
                                    numericSelectOptions={constructionYearLoadFunction}
                                />
                                <ParametrizedField
                                    contextName={fieldsName.vehicleOwnerBirthYear}
                                    quotationName={'quotation.mtplContract.'}
                                    context={QuotationCardContext}
                                    numericSelectOptions={getOwnerBirthYearOptions}
                                />
                                <ParametrizedField
                                    contextName={fieldsName.vehicleNextToDate}
                                    quotationName={'quotation.mtplContract.'}
                                    context={QuotationCardContext}
                                    onChangeListener={(option: string) => {
                                        calculateMTPLDuration({
                                            startDate: formProps.values.quotation.mtplContract.startDate ? moment(formProps.values.quotation.mtplContract.startDate).format('YYYY-MM-DD'): null,
                                            requiredTO: formProps.values.quotation.mtplContract.vehicle.requiredTO,
                                            nextToDate: option
                                        }).then((response: CalculatedMTPLDuration) => updateMTPLDuration(response, formProps));
                                    }}
                                />
                                <ParametrizedField
                                    contextName={fieldsName.startDate}
                                    quotationName={'quotation.mtplContract.'}
                                    context={QuotationCardContext}
                                    onChangeListener={(option: string) => {

                                        calculateMTPLDuration({
                                            startDate: option,
                                            requiredTO: formProps.values.quotation.mtplContract.vehicle.requiredTO,
                                            nextToDate: formProps.values.quotation.mtplContract.vehicle.nextToDate ? moment(formProps.values.quotation.mtplContract.vehicle.nextToDate).format('YYYY-MM-DD'): null
                                        }).then((response: CalculatedMTPLDuration) => updateMTPLDuration(response, formProps));
                                    }}
                                />
                            </div>
                            <div className="mtpl-tariffication-input__form__submit">
                                <div className="mtpl-tariffication-input__form__submit__button fluid">
                                    <Button
                                        label={strings.calculate}
                                        onClickListener={() => {
                                            formProps.handleSubmit();
                                        }}
                                        disabled={!nextIsAvailable}
                                    />
                                </div>

                                <div
                                    className="mtpl-tariffication-input__form__submit__check-data"
                                    hidden={selectedView !== 'search'}
                                >
                                <span className="mtpl-tariffication-input__form__submit__check-data__header">
                                    {searchBy === "plateNumber" && strings.tariffication.checkData.plateNumber.title}
                                    {searchBy === "phoneNumber" && strings.tariffication.checkData.phoneNumber.title}
                                </span>
                                <span>
                                    {searchBy === "plateNumber" && strings.tariffication.checkData.plateNumber.text}
                                    {searchBy === "phoneNumber" && strings.tariffication.checkData.phoneNumber.text}
                                </span>
                                </div>

                                <div
                                    className="mtpl-tariffication-input__form__submit__otk-pdf"
                                    hidden={formProps.getFieldMeta("quotation.mtplContract.vehicle.requiredTO").value !== true}
                                >
                                    <OutputLink
                                        label={strings.vehicleOtk}
                                        iconPos={"left"}
                                        href={`${process.env.PUBLIC_URL}/resources/help_otk.pdf`}
                                        icon={<img src={otkIcon} alt='' />}
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </Formik>
    )

};
